import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Typography,
  TextField,
  Button,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import configuracionService, { ConfiguracionDto } from '../../services/configuracionService';

const LIST_PATH = '/configuraciones/parametros';

const ParametroSistemaForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();

  const usuario = searchParams.get('usuario') ?? '';
  const seccion = (searchParams.get('seccion') ?? '').trim();
  const campo = (searchParams.get('campo') ?? '').trim();

  const [valor, setValor] = useState('');
  const [comentario, setComentario] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadConfiguracion = useCallback(async () => {
    if (!seccion || !campo) {
      enqueueSnackbar('Faltan sección o campo para editar el parámetro', { variant: 'error' });
      navigate(LIST_PATH);
      return;
    }

    try {
      setLoading(true);
      const config = await configuracionService.getByKey(seccion, campo, usuario);
      if (!config) {
        enqueueSnackbar('No se encontró el parámetro solicitado', { variant: 'error' });
        navigate(LIST_PATH);
        return;
      }
      setValor(config.valor ?? '');
      setComentario(config.comentario ?? '');
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        enqueueSnackbar('No tiene permisos para ver este parámetro', { variant: 'error' });
      } else {
        enqueueSnackbar('Error al cargar el parámetro', { variant: 'error' });
      }
      navigate(LIST_PATH);
    } finally {
      setLoading(false);
    }
  }, [seccion, campo, usuario, enqueueSnackbar, navigate]);

  useEffect(() => {
    loadConfiguracion();
  }, [loadConfiguracion]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!seccion || !campo) return;

    try {
      setSaving(true);
      const dto: ConfiguracionDto = {
        usuario,
        seccion,
        campo,
        valor: valor.trim() || null,
        comentario: comentario.trim() || null,
      };
      await configuracionService.updateConfiguracion(dto);
      enqueueSnackbar('Parámetro actualizado correctamente', { variant: 'success' });
      navigate(LIST_PATH);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response?.status;
      if (status === 403) {
        enqueueSnackbar('No tiene permisos para modificar parámetros del sistema', { variant: 'error' });
      } else if (status === 404) {
        enqueueSnackbar('El parámetro ya no existe', { variant: 'error' });
      } else {
        enqueueSnackbar('Error al guardar el parámetro', { variant: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflow: 'hidden', maxWidth: 800, mx: 'auto' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2', mb: 3 }}>
            Editar parámetro
          </Typography>

          <Box component="form" onSubmit={handleSubmit}>
            <Stack spacing={2}>
              <TextField
                label="Usuario"
                value={usuario.trim() ? usuario : '(global)'}
                disabled
                fullWidth
              />
              <TextField label="Sección" value={seccion} disabled fullWidth />
              <TextField label="Campo" value={campo} disabled fullWidth />
              <TextField
                label="Valor"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                inputProps={{ maxLength: 1000 }}
                helperText={`${valor.length}/1000`}
              />
              <TextField
                label="Comentario"
                value={comentario}
                onChange={(e) => setComentario(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                inputProps={{ maxLength: 500 }}
                helperText={`${comentario.length}/500`}
              />
              <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ pt: 1 }}>
                <Button variant="outlined" onClick={() => navigate(LIST_PATH)} disabled={saving}>
                  Cancelar
                </Button>
                <Button type="submit" variant="contained" color="primary" disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              </Stack>
            </Stack>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ParametroSistemaForm;
